/**
 * Brief Builder - one guided path.
 *
 * A brief is a title, the areas it covers, a set of references, an idea,
 * shoot details, chosen talent and any extra sections added afterwards.
 */

export type AreaId =
  | "casting"
  | "photography"
  | "styling"
  | "hair_makeup"
  | "production"
  | "concept"
  | "treatment";

export interface AreaDefinition {
  id: AreaId;
  label: string;
  blurb: string;
  /** Sections dropped into the brief when this area is chosen. */
  sections: SectionType[];
}

export const AREAS: AreaDefinition[] = [
  {
    id: "casting",
    label: "A model casting brief",
    blurb: "Send it to agencies so they can put talent forward.",
    sections: ["casting"],
  },
  {
    id: "photography",
    label: "Brief a photographer",
    blurb: "Direction, deliverables and how it should be shot.",
    sections: ["photography", "deliverables"],
  },
  {
    id: "styling",
    label: "Styling",
    blurb: "Looks, silhouettes, sourcing and fittings.",
    sections: ["styling"],
  },
  {
    id: "hair_makeup",
    label: "Hair + Makeup",
    blurb: "Beauty direction and changes across the day.",
    sections: ["hair_makeup"],
  },
  {
    id: "production",
    label: "Location + production",
    blurb: "Where it happens, who's attached, what the crew needs.",
    sections: ["production"],
  },
  {
    id: "concept",
    label: "Just the concept",
    blurb: "Shape the idea first, add the rest whenever you like.",
    sections: [],
  },
  {
    id: "treatment",
    label: "Responding to brief",
    blurb: "Building a treatment for a client pitch.",
    sections: ["deliverables", "usage", "budget", "notes"],
  },
];

export function areaDef(id: string): AreaDefinition | undefined {
  return AREAS.find((a) => a.id === id);
}

/* --------------------------------------------------------------- audience */

export type AudienceId = "agencies" | "crew" | "client" | "photographer" | "internal";

export interface AudienceDefinition {
  id: AudienceId;
  label: string;
  blurb: string;
}

export const AUDIENCES: AudienceDefinition[] = [
  { id: "agencies", label: "Model agencies", blurb: "So they can put talent forward." },
  { id: "crew", label: "The crew", blurb: "Photographer, stylist, hair and makeup, production." },
  { id: "client", label: "A client", blurb: "To sign off the idea before anything is booked." },
  { id: "photographer", label: "A photographer or studio", blurb: "Direction and deliverables." },
  { id: "internal", label: "My own team", blurb: "Keep everyone working from one page." },
];

export function audienceDef(id: string): AudienceDefinition | undefined {
  return AUDIENCES.find((a) => a.id === id);
}

/* ------------------------------------------------------- reference groups */

/**
 * Where a reference image belongs - the whole project, one avenue, the intro
 * page's style guide, or a page the user added themselves (`page_<id>`).
 */
export type ReferenceArea = "overall" | AreaId | "intro" | (string & {});

export interface ReferenceGroup {
  id: ReferenceArea;
  label: string;
  hint: string;
}

const AREA_REFERENCE_HINTS: Record<AreaId, string> = {
  casting: "Faces, energy, the kind of people you picture.",
  photography: "Light, framing, grade, movement.",
  styling: "Silhouettes, fabrics, colour, accessories.",
  hair_makeup: "Skin, hair, beauty details.",
  production: "Locations, sets, spaces, textures.",
  concept: "Anything that carries the feeling.",
  treatment: "References that support the response and the win.",
};

/** The overall board first, then one board per avenue the brief covers. */
export function referenceGroups(areas: AreaId[]): ReferenceGroup[] {
  const groups: ReferenceGroup[] = [
    { id: "overall", label: "Overall idea", hint: "The mood for the whole project." },
  ];
  for (const id of areas) {
    if (id === "concept") continue;
    const def = areaDef(id);
    if (!def) continue;
    groups.push({
      id,
      label: def.label.replace(/^A model /, "").replace(/^Brief a /, "").replace(/^Just the /, ""),
      hint: AREA_REFERENCE_HINTS[id],
    });
  }
  return groups;
}

export type SectionType =
  | "casting"
  | "photography"
  | "styling"
  | "collection"
  | "hair"
  | "makeup"
  | "hair_makeup"
  | "production"
  | "schedule"
  | "team"
  | "contact"
  | "deliverables"
  | "usage"
  | "budget"
  | "notes"
  | "lighting_guide"
  | "lighting_planner"
  /** A blank page images can be dropped straight onto. */
  | "gallery"
  /** A headed page for reference images only - no body text. */
  | "references"
  /** A blank page with a heading and free text. */
  | "text";

/** Page types the user can add more than one of. */
export const REPEATABLE_SECTIONS: SectionType[] = ["gallery", "text", "references", "collection", "contact"];

export interface SectionDefinition {
  type: SectionType;
  label: string;
  kicker: string;
  /** One line explaining what this page of the brief is for. */
  description: string;
  placeholder: string;
}

export const SECTIONS: SectionDefinition[] = [
  {
    type: "casting",
    description: "Who should be in front of the camera, and why they fit the idea.",
    label: "Casting",
    kicker: "The faces",
    placeholder: "Who you're looking for - look, age, energy, how many.",
  },
  {
    type: "photography",
    description: "How the images should be made - light, framing and movement.",
    label: "Photography",
    kicker: "The image",
    placeholder: "Lighting, lens, framing, movement, grade.",
  },
  {
    type: "styling",
    description: "The wardrobe story and how it is put together on the day.",
    label: "Styling",
    kicker: "The wardrobe",
    placeholder: "Silhouettes, fabrics, colour story, sourcing.",
  },
  {
    type: "collection",
    description: "A focused contact sheet and hero image for the collection story.",
    label: "Collection",
    kicker: "The collection",
    placeholder: "Describe the season, collection and its visual inspiration.",
  },
  {
    type: "hair_makeup",
    description: "A clear beauty direction split into hair and makeup.",
    label: "Hair + Makeup",
    kicker: "The beauty",
    placeholder: "Look: one-line summary\n\nHair\n- ...\n\nMakeup\n- Skin: ...\n- Brows: ...",
  },
  {
    type: "hair",
    description: "Hair direction on its own page.",
    label: "Hair",
    kicker: "The hair",
    placeholder: "Texture, shape, parting, finish, changes between looks.",
  },
  {
    type: "makeup",
    description: "Makeup direction on its own page.",
    label: "Makeup",
    kicker: "The makeup",
    placeholder: "Skin, brows, eyes, lip, and how it reads on camera.",
  },
  {
    type: "production",
    description: "Everything the day needs to run - place, people and logistics.",
    label: "Location + Production",
    kicker: "The day",
    placeholder: "Access, crew, permits, kit, catering.",
  },
  {
    type: "schedule",
    description: "The day hour by hour, from call time to wrap.",
    label: "Schedule",
    kicker: "The running order",
    placeholder: "8:00 Call\n9:00 Hair + makeup\n11:00 First look...",
  },
  {
    type: "team",
    description: "Who is attached, and who is still to be found.",
    label: "Team + Credits",
    kicker: "The people",
    placeholder: "Photographer, stylist, hair, makeup, production, assistants.",
  },
  {
    type: "contact",
    description: "Who to speak to, and how to reach them.",
    label: "Contact",
    kicker: "Get in touch",
    placeholder: "Name, email, phone, studio.",
  },
  {
    type: "deliverables",
    description: "What is handed over at the end, and when.",
    label: "Deliverables",
    kicker: "The output",
    placeholder: "Number of images, crops, cuts, turnaround.",
  },
  {
    type: "usage",
    description: "Where the work runs, for how long and in which territories.",
    label: "Usage",
    kicker: "The terms",
    placeholder: "Where it runs, for how long, which territory.",
  },
  {
    type: "budget",
    description: "The money set aside, so everyone works to the same number.",
    label: "Budget",
    kicker: "The number",
    placeholder: "A range is fine - it sets expectations early.",
  },
  {
    type: "notes",
    description: "Anything else the team should know before the day.",
    label: "Notes",
    kicker: "Anything else",
    placeholder: "Anything the team should know.",
  },
  {
    type: "lighting_guide",
    description: "The lighting direction - sources, modifiers and mood.",
    label: "Lighting Guide",
    kicker: "The light",
    placeholder: "Key light, modifiers, contrast, colour temperature, references.",
  },
  {
    type: "lighting_planner",
    description: "Setups for each look, so the day moves quickly.",
    label: "Lighting Planner",
    kicker: "The setups",
    placeholder: "Look 1: ...\nLook 2: ...\nKit list and power notes.",
  },
  {
    type: "gallery",
    description: "A blank page. Drop images straight onto it.",
    label: "Image page",
    kicker: "Images",
    placeholder: "",
  },
  {
    type: "text",
    description: "A blank page for anything written.",
    label: "Text page",
    kicker: "Words",
    placeholder: "Write anything here.",
  },
  {
    type: "references",
    description: "A page of reference images - header and images only.",
    label: "Reference Images",
    kicker: "The references",
    placeholder: "",
  },
];

/** Pages that carry their own images as well as words. */
export const VISUAL_SECTIONS: SectionType[] = [
  "casting",
  "photography",
  "styling",
  "collection",
  "hair",
  "makeup",
  "hair_makeup",
  "lighting_guide",
  "lighting_planner",
  "production",
  "team",
  "gallery",
  "references",
];

/* ------------------------------------------------------------- brief kind */

/** What the user is building. It decides which pages get pre-built. */
export type BriefKind =
  | "creative"
  | "photography"
  | "styling_brief"
  | "hair_makeup_brief"
  | "response"
  | "casting"
  | "concept"
  | "portfolio"
  | "pitch"
  | "blank"
  | "production_bible"
  | "continuity"
  | "location_scout";

export interface BriefKindDefinition {
  id: BriefKind;
  label: string;
  blurb: string;
  orientation: "landscape" | "portrait";
  bestFor: string;
  /** The a la carte menu shown in setup. */
  menu: SectionType[];
  /** Ticked for them to start with. */
  preselected: SectionType[];
  /** The area keys kept for reference boards and review. */
  areas: AreaId[];
}

export const BRIEF_KINDS: BriefKindDefinition[] = [
  {
    id: "creative",
    label: "Creative Brief",
    blurb: "Brief the full creative and production team, from the idea through to delivery.",
    orientation: "landscape",
    bestFor: "Crew briefing",
    menu: [
      "references",
      "photography",
      "styling",
      "hair",
      "makeup",
      "hair_makeup",
      "casting",
      "production",
      "schedule",
      "deliverables",
      "usage",
      "budget",
      "team",
      "contact",
    ],
    preselected: ["references", "photography", "styling", "hair_makeup", "casting", "deliverables", "usage", "production"],
    areas: ["concept"],
  },
  {
    id: "photography",
    label: "Photography Brief",
    blurb: "Set the visual language, lighting, framing, locations and final image requirements.",
    orientation: "landscape",
    bestFor: "Photographers",
    menu: ["references", "casting", "photography", "styling", "collection", "hair", "makeup", "production", "schedule", "deliverables", "usage", "contact"],
    preselected: ["references", "casting", "photography", "styling", "collection", "hair", "makeup", "production", "schedule", "deliverables", "usage", "contact"],
    areas: ["concept", "photography", "styling", "hair_makeup", "casting", "production"],
  },
  {
    id: "styling_brief",
    label: "Styling Brief",
    blurb: "Shape the wardrobe story, references, cast, fittings, schedule and sourcing notes.",
    orientation: "landscape",
    bestFor: "Wardrobe teams",
    menu: ["references", "styling", "casting", "hair_makeup", "production", "schedule", "team", "budget", "notes", "contact"],
    preselected: ["references", "styling", "casting", "schedule", "notes"],
    areas: ["concept", "styling", "casting"],
  },
  {
    id: "hair_makeup_brief",
    label: "Hair + Makeup Brief",
    blurb: "Define beauty references, hair and makeup direction, talent needs and continuity notes.",
    orientation: "landscape",
    bestFor: "Beauty teams",
    menu: ["references", "hair", "makeup", "hair_makeup", "casting", "photography", "schedule", "team", "notes", "contact"],
    preselected: ["references", "hair", "makeup", "casting", "notes"],
    areas: ["concept", "hair_makeup", "casting"],
  },
  {
    id: "casting",
    label: "Casting",
    blurb: "Set the roles, visual direction, dates, usage and budget, then invite talent submissions.",
    orientation: "landscape",
    bestFor: "Agency casting",
    menu: ["casting", "references", "styling", "team", "schedule", "usage", "budget", "contact"],
    preselected: ["casting", "usage", "budget"],
    areas: ["casting"],
  },
  {
    id: "concept",
    label: "Craft a Concept",
    blurb: "Turn an early idea and references into a concise visual direction that can grow later.",
    orientation: "landscape",
    bestFor: "Early ideas",
    menu: ["gallery", "references", "photography", "styling", "notes"],
    preselected: ["gallery"],
    areas: ["concept"],
  },
  {
    id: "portfolio",
    label: "Portfolio",
    blurb: "Build an image-led presentation of your work, credits, services and contact details.",
    orientation: "landscape",
    bestFor: "Self-promotion",
    menu: ["gallery", "references", "photography", "styling", "team", "notes", "contact"],
    preselected: ["gallery", "contact"],
    areas: ["concept"],
  },
  {
    id: "response",
    label: "Respond to a Brief",
    blurb: "Upload or paste the client brief, then let Focus AI help shape your response deck.",
    orientation: "landscape",
    bestFor: "Client approval",
    menu: [
      "references",
      "photography",
      "styling",
      "hair_makeup",
      "casting",
      "production",
      "schedule",
      "deliverables",
      "usage",
      "budget",
      "team",
      "contact",
    ],
    preselected: ["deliverables", "budget"],
    areas: ["treatment"],
  },
  {
    id: "pitch",
    label: "Pitch",
    blurb: "Present the idea, approach, creative team, deliverables and budget to win approval.",
    orientation: "landscape",
    bestFor: "New business",
    menu: ["references", "photography", "styling", "casting", "production", "team", "deliverables", "usage", "budget", "contact"],
    preselected: ["photography", "team", "deliverables", "budget", "contact"],
    areas: ["concept", "treatment"],
  },
  {
    id: "blank",
    label: "Blank",
    blurb: "Open one empty page with no assumptions and build the document entirely your way.",
    orientation: "landscape",
    bestFor: "Anything else",
    menu: [
      "text",
      "gallery",
      "references",
      "photography",
      "styling",
      "hair",
      "makeup",
      "hair_makeup",
      "lighting_guide",
      "lighting_planner",
      "casting",
      "production",
      "schedule",
      "deliverables",
      "usage",
      "budget",
      "team",
      "notes",
      "contact",
    ],
    preselected: ["text"],
    areas: [],
  },
  {
    id: "production_bible",
    label: "Production Bible",
    blurb: "Organize locations, crew, schedule, lighting, contacts and production notes.",
    orientation: "portrait",
    bestFor: "Crew handoff",
    menu: ["production", "schedule", "team", "lighting_guide", "lighting_planner", "casting", "references", "styling", "contact", "notes"],
    preselected: ["production", "schedule", "team", "lighting_planner", "contact"],
    areas: ["production", "photography", "styling", "hair_makeup"],
  },
  {
    id: "continuity",
    label: "Fitting + Continuity",
    blurb: "Track looks, garments, sizes, fittings, reference images and continuity notes.",
    orientation: "portrait",
    bestFor: "Wardrobe teams",
    menu: ["styling", "gallery", "casting", "schedule", "team", "notes", "contact"],
    preselected: ["styling", "gallery", "casting", "notes"],
    areas: ["styling", "casting"],
  },
  {
    id: "location_scout",
    label: "Location Scout",
    blurb: "Compare locations with images, access, permits, facilities, parking and contacts.",
    orientation: "portrait",
    bestFor: "Location decisions",
    menu: ["production", "gallery", "schedule", "team", "budget", "notes", "contact"],
    preselected: ["production", "gallery", "budget", "contact"],
    areas: ["production", "concept"],
  },
];

export function kindDef(id: string | undefined): BriefKindDefinition {
  return BRIEF_KINDS.find((k) => k.id === id) || BRIEF_KINDS[0];
}

/** Older briefs saved before the four kinds existed. */
export function inferKind(doc: { kind?: BriefKind; areas: AreaId[] }): BriefKind {
  if (doc.kind) return doc.kind;
  if (doc.areas.includes("treatment")) return "response";
  if (doc.areas.length === 1 && doc.areas[0] === "casting") return "casting";
  if (doc.areas.length === 1 && doc.areas[0] === "concept") return "concept";
  return "creative";
}

export function sectionDef(type: string): SectionDefinition {
  return (
    SECTIONS.find((s) => s.type === type) ||
    SECTIONS.find((s) => s.type === "notes") ||
    SECTIONS[0]
  );
}

/** The board a page of images belongs to. */
export function areaForSection(section: { id: string; type: SectionType }): ReferenceArea {
  return `page_${section.id}`;
}

/** How an image is framed inside its slot on the page. */
export interface ReferenceCrop {
  /** Focal point, 0 - 100 across and down the image. */
  x: number;
  y: number;
  /** 1 = fits the slot, higher zooms in. */
  zoom: number;
  ratio: "portrait" | "square" | "landscape";
  fit: "fill" | "fit";
  /** Non-destructive image treatment applied in the brief only. */
  rotation?: number;
  flipX?: boolean;
  flipY?: boolean;
  brightness?: number;
  contrast?: number;
  saturation?: number;
  warmth?: number;
  opacity?: number;
  filter?: ImageFilterPreset;
  tint?: string;
  tintStrength?: number;
  /**
   * Free drag offsets, as a percentage of the frame size. Unlike the focal
   * point above - which only moves an axis the image overflows - these shift
   * the picture in any direction, so double-click drag always pans freely.
   */
  offsetX?: number;
  offsetY?: number;
}

export type ImageFilterPreset = "none" | "mono" | "soft" | "warm" | "cool" | "contrast" | "fade";
export type ImageTreatment = ReferenceCrop;

export function defaultCrop(): ReferenceCrop {
  return { x: 50, y: 50, zoom: 1, ratio: "portrait", fit: "fill" };
}

export function normalizeCrop(raw: unknown): ReferenceCrop {
  const base = defaultCrop();
  const c = (raw || {}) as Partial<ReferenceCrop>;
  return {
    x: typeof c.x === "number" ? Math.min(100, Math.max(0, c.x)) : base.x,
    y: typeof c.y === "number" ? Math.min(100, Math.max(0, c.y)) : base.y,
    zoom: typeof c.zoom === "number" ? Math.min(3, Math.max(1, c.zoom)) : base.zoom,
    ratio: c.ratio === "square" || c.ratio === "landscape" ? c.ratio : base.ratio,
    fit: c.fit === "fit" ? "fit" : "fill",
    rotation: typeof c.rotation === "number" ? Math.min(180, Math.max(-180, c.rotation)) : 0,
    flipX: c.flipX === true,
    flipY: c.flipY === true,
    brightness: typeof c.brightness === "number" ? Math.min(200, Math.max(0, c.brightness)) : 100,
    contrast: typeof c.contrast === "number" ? Math.min(200, Math.max(0, c.contrast)) : 100,
    saturation: typeof c.saturation === "number" ? Math.min(200, Math.max(0, c.saturation)) : 100,
    warmth: typeof c.warmth === "number" ? Math.min(100, Math.max(-100, c.warmth)) : 0,
    opacity: typeof c.opacity === "number" ? Math.min(100, Math.max(0, c.opacity)) : 100,
    filter: c.filter === "mono" || c.filter === "soft" || c.filter === "warm" || c.filter === "cool" || c.filter === "contrast" || c.filter === "fade" ? c.filter : "none",
    tint: cleanColor(c.tint),
    tintStrength: typeof c.tintStrength === "number" ? Math.min(100, Math.max(0, c.tintStrength)) : 0,
    offsetX: typeof c.offsetX === "number" ? Math.min(100, Math.max(-100, c.offsetX)) : 0,
    offsetY: typeof c.offsetY === "number" ? Math.min(100, Math.max(-100, c.offsetY)) : 0,
  };
}

export function imageTreatmentStyle(raw?: Partial<ImageTreatment>) {
  const treatment = normalizeCrop(raw);
  const presets: Record<ImageFilterPreset, { brightness: number; contrast: number; saturation: number; sepia: number; hue: number }> = {
    none: { brightness: 100, contrast: 100, saturation: 100, sepia: 0, hue: 0 },
    mono: { brightness: 100, contrast: 108, saturation: 0, sepia: 0, hue: 0 },
    soft: { brightness: 106, contrast: 88, saturation: 88, sepia: 5, hue: 0 },
    warm: { brightness: 102, contrast: 102, saturation: 108, sepia: 18, hue: -8 },
    cool: { brightness: 100, contrast: 103, saturation: 92, sepia: 8, hue: 165 },
    contrast: { brightness: 98, contrast: 132, saturation: 108, sepia: 0, hue: 0 },
    fade: { brightness: 108, contrast: 78, saturation: 72, sepia: 9, hue: 0 },
  };
  const preset = presets[treatment.filter || "none"];
  const brightness = treatment.brightness ?? 100;
  const contrast = treatment.contrast ?? 100;
  const saturation = treatment.saturation ?? 100;
  const warmth = treatment.warmth ?? 0;
  return {
    objectFit: treatment.fit === "fit" ? ("contain" as const) : ("cover" as const),
    objectPosition: `${treatment.x}% ${treatment.y}%`,
    opacity: (treatment.opacity ?? 100) / 100,
    filter: `brightness(${(brightness * preset.brightness) / 100}%) contrast(${(contrast * preset.contrast) / 100}%) saturate(${(saturation * preset.saturation) / 100}%) sepia(${preset.sepia}%) hue-rotate(${preset.hue + warmth * 0.18}deg)`,
    // Translate comes first so the offset is measured against the unscaled
    // frame-sized image: 100 shifts the picture by its own width/height.
    transform: `translate(${treatment.offsetX || 0}%, ${treatment.offsetY || 0}%) scale(${treatment.zoom}) scaleX(${treatment.flipX ? -1 : 1}) scaleY(${treatment.flipY ? -1 : 1}) rotate(${treatment.rotation || 0}deg)`,
  };
}

export const CROP_RATIO_CLASS: Record<ReferenceCrop["ratio"], string> = {
  portrait: "aspect-[4/5]",
  square: "aspect-square",
  landscape: "aspect-[4/3]",
};

export interface BriefReference {
  id: string;
  /** Which board this image belongs to. */
  area: ReferenceArea;
  /** Displayable image source - a signed URL when saved, a data URL when signed out. */
  url: string;
  /** Storage path, only for saved briefs. */
  path?: string;
  /** How it is framed on the page. */
  crop?: ReferenceCrop;
  /** Optional caption - photographer, campaign, year. */
  credit?: string;
  /** Which slot on a multi-image page this belongs to - keeps each box independent. */
  slotIndex?: number;
}

/**
 * The image that best represents a brief in a list - the cover photo when one
 * has been set, otherwise the first reference of any kind.
 */
export function briefPreviewImage(references: BriefReference[]): BriefReference | null {
  if (!references?.length) return null;
  const covers = references
    .filter((r) => r.area === "cover" && r.url)
    .sort((a, b) => (a.slotIndex ?? 0) - (b.slotIndex ?? 0));
  return covers[0] || references.find((r) => r.url) || null;
}

/** Where a face on the casting board came from. */
export type TalentSource = "focus" | "manual";

/** Where the talent sits in your thinking. */
export type TalentStatus = "first" | "option" | "backup" | "out";

export const TALENT_STATUSES: { id: TalentStatus; label: string }[] = [
  { id: "first", label: "First choice" },
  { id: "option", label: "Option" },
  { id: "backup", label: "Backup" },
  { id: "out", label: "Out" },
];

export interface BriefTalent {
  id: string;
  name: string;
  agency: string;
  city: string;
  traits: string[];
  photo: string;
  /** Focus talent, or someone added by hand. */
  source?: TalentSource;
  /** Agency or portfolio link - used for manually added talent. */
  link?: string;
  /** The note shown under the face in the deck. */
  note?: string;
  /** The role/character they are being considered for. */
  role?: string;
  status?: TalentStatus;
  /** Optional day rate, free text so any currency works. */
  rate?: string;
  /** Extra portfolio images chosen for this brief. */
  images?: string[];
  /** Height, bust, waist, hips, shoe, hair, eyes - shown when present. */
  measurements?: Record<string, string>;
}

export interface CastingWorkspaceState {
  /** Talent still being considered and excluded from presentation pages. */
  poolIds: string[];
  /** User-defined role lanes available in the casting desk. */
  roles: string[];
  /** Persistent two-to-four person comparison selection. */
  compareIds: string[];
  /** Where each candidate entered the workspace. */
  provenance: Record<string, string>;
}

export function defaultCastingWorkspace(): CastingWorkspaceState {
  return { poolIds: [], roles: ["Lead", "Supporting", "Background"], compareIds: [], provenance: {} };
}

export function normalizeCastingWorkspace(raw: unknown): CastingWorkspaceState {
  const value = (raw || {}) as Partial<CastingWorkspaceState>;
  const defaults = defaultCastingWorkspace();
  return {
    poolIds: Array.isArray(value.poolIds) ? value.poolIds.map(String).filter(Boolean) : [],
    roles: Array.isArray(value.roles) && value.roles.length
      ? value.roles.map(String).map((role) => role.trim()).filter(Boolean)
      : defaults.roles,
    compareIds: Array.isArray(value.compareIds) ? value.compareIds.map(String).filter(Boolean).slice(0, 4) : [],
    provenance: value.provenance && typeof value.provenance === "object"
      ? Object.fromEntries(Object.entries(value.provenance).map(([key, entry]) => [key, String(entry)]))
      : {},
  };
}

export function normalizeTalent(raw: unknown): BriefTalent {
  const t = (raw || {}) as Partial<BriefTalent> & Record<string, unknown>;
  const out: BriefTalent = {
    id: String(t.id || ""),
    name: String(t.name || ""),
    agency: String(t.agency || ""),
    city: String(t.city || ""),
    traits: Array.isArray(t.traits) ? (t.traits as unknown[]).map(String).filter(Boolean) : [],
    photo: String(t.photo || ""),
  };
  out.source = t.source === "manual" ? "manual" : "focus";
  if (typeof t.link === "string" && t.link.trim()) out.link = t.link.trim();
  if (typeof t.note === "string" && t.note.trim()) out.note = t.note;
  if (typeof t.role === "string" && t.role.trim()) out.role = t.role.trim();
  if (TALENT_STATUSES.some((s) => s.id === t.status)) out.status = t.status as TalentStatus;
  if (typeof t.rate === "string" && t.rate.trim()) out.rate = t.rate.trim();
  if (Array.isArray(t.images)) out.images = (t.images as unknown[]).map(String).filter(Boolean);
  if (t.measurements && typeof t.measurements === "object") {
    const entries = Object.entries(t.measurements as Record<string, unknown>)
      .filter(([, v]) => typeof v === "string" && String(v).trim())
      .map(([k, v]) => [k, String(v)] as const);
    if (entries.length) out.measurements = Object.fromEntries(entries);
  }
  return out;
}

/* ------------------------------------------------------------ cover facts */

/** One line of detail on the cover page - brand, client, contact, website... */
export type CoverFieldType = "text" | "long" | "url" | "email" | "tel";

export interface CoverDetail {
  id: string;
  label: string;
  value: string;
  type?: CoverFieldType;
}

export interface CoverFieldDefinition {
  label: string;
  placeholder: string;
  group: string;
  type?: CoverFieldType;
}

/** Optional details the user can add to the cover, as much or as little as they like. */
export const COVER_FIELDS: CoverFieldDefinition[] = [
  // The project
  { label: "Brand", placeholder: "The brand this is for", group: "The project" },
  { label: "Client", placeholder: "Who is commissioning it", group: "The project" },
  { label: "Project type", placeholder: "Campaign, editorial, lookbook, e-comm", group: "The project" },
  { label: "Season", placeholder: "Spring / Summer 26", group: "The project" },
  { label: "Market", placeholder: "Where it runs - city, country, global", group: "The project" },
  { label: "Deliverables", placeholder: "12 stills, 2 films, 30 e-comm", group: "The project", type: "long" },
  { label: "Usage", placeholder: "Digital + social, 12 months, US", group: "The project", type: "long" },
  { label: "Budget", placeholder: "Total or per-day range", group: "The project" },
  { label: "Deadline", placeholder: "When it needs to be delivered", group: "The project" },
  { label: "Shoot dates", placeholder: "3 - 4 March", group: "The project" },
  { label: "Location", placeholder: "Studio, city or country", group: "The project" },

  // The team
  { label: "Talent attached", placeholder: "Names already confirmed", group: "The team" },
  { label: "Photographer", placeholder: "Attached or in conversation", group: "The team" },
  { label: "Director", placeholder: "Attached or in conversation", group: "The team" },
  { label: "Stylist", placeholder: "Attached or in conversation", group: "The team" },
  { label: "Hair + Makeup", placeholder: "Attached or in conversation", group: "The team" },
  { label: "Production", placeholder: "Production company or producer", group: "The team" },
  { label: "Agency", placeholder: "Creative or media agency", group: "The team" },
  { label: "Casting", placeholder: "Casting director or partner", group: "The team" },

  // Contact
  { label: "Prepared by", placeholder: "You, or your company", group: "Contact" },
  { label: "Contact", placeholder: "Who to speak to", group: "Contact" },
  { label: "Email", placeholder: "name@company.com", group: "Contact", type: "email" },
  { label: "Phone", placeholder: "+1 555 000 0000", group: "Contact", type: "tel" },
  { label: "Website", placeholder: "https://", group: "Contact", type: "url" },
  { label: "Instagram", placeholder: "@handle", group: "Contact" },
  { label: "Address", placeholder: "Street, city, country", group: "Contact", type: "long" },
  { label: "Notes", placeholder: "Anything else worth saying up front", group: "Contact", type: "long" },
];

export const COVER_FIELD_GROUPS = ["The project", "The team", "Contact"] as const;


/* ---------------------------------------------------------- presentation */

/** How the cover page is laid out. */
export type CoverStyle = "editorial" | "statement" | "gallery";

export interface CoverStyleDefinition {
  id: CoverStyle;
  label: string;
  blurb: string;
}

export const COVER_STYLES: CoverStyleDefinition[] = [
  {
    id: "editorial",
    label: "Lookbook",
    blurb: "Masthead title across the top, one big image below, details along the foot.",
  },
  {
    id: "statement",
    label: "Statement",
    blurb: "Full-bleed image with an oversized title set straight over the top.",
  },
  {
    id: "gallery",
    label: "Split",
    blurb: "Title and contact details on the left, a full-height image on the right.",
  },
];


/** Portrait reads like a document, landscape reads like a deck. */
export type BriefOrientation = "portrait" | "landscape";

export interface EndPageLink {
  id: string;
  label: string;
  url: string;
}

export interface EndPage {
  enabled: boolean;
  heading: string;
  message: string;
  links: EndPageLink[];
}

/** The optional page where a brand, studio or photographer introduces itself. */
export interface IntroPage {
  enabled: boolean;
  heading: string;
  body: string;
  links: EndPageLink[];
}

/** Page and text colour - empty values mean "use the default look". */
export interface PageTheme {
  bg?: string;
  text?: string;
}

/** Suggested page colours, kept small and editorial. */
export const PAGE_COLORS: { label: string; bg: string; text: string }[] = [
  { label: "Paper", bg: "#FFFFFF", text: "#202124" },
  { label: "Bone", bg: "#F6F3EE", text: "#221F1C" },
  { label: "Mist", bg: "#F1F2F5", text: "#202124" },
  { label: "Blush", bg: "#F7EDEA", text: "#2A1F1D" },
  { label: "Sage", bg: "#ECF0EA", text: "#1E2420" },
  { label: "Ink", bg: "#141417", text: "#F5F5F7" },
  { label: "Indigo", bg: "#211E4F", text: "#F2F1FF" },
  { label: "Clay", bg: "#2C2422", text: "#F6EFEA" },
];

/** Personal touches on a single piece of text - click it in the editor to change. */
export interface TextStyle {
  /** Size multiplier against the default for that text, 0.6 - 2.4. */
  size?: number;
  /** Optional face for one text element, independent of the document pairing. */
  fontFamily?: "display" | "body" | "mono";
  align?: "left" | "center" | "right";
  bold?: boolean;
  italic?: boolean;
  /** ALL CAPS. */
  caps?: boolean;
  color?: string;
}

export function normalizeTextStyle(raw: unknown): TextStyle {
  const s = (raw || {}) as Partial<TextStyle>;
  const out: TextStyle = {};
  if (typeof s.size === "number" && s.size > 0) out.size = Math.min(2.4, Math.max(0.6, s.size));
  if (s.fontFamily === "display" || s.fontFamily === "body" || s.fontFamily === "mono") out.fontFamily = s.fontFamily;
  if (s.align === "center" || s.align === "right" || s.align === "left") out.align = s.align;
  if (s.bold) out.bold = true;
  if (s.italic) out.italic = true;
  if (s.caps) out.caps = true;
  const color = cleanColor(s.color);
  if (color) out.color = color;
  return out;
}

export function isTextStyled(style: TextStyle | undefined): boolean {
  return !!style && Object.keys(style).length > 0;
}

/* ---------------------------------------------------------- casting board */

/** One face per page, or a grid of faces on a page. */
export interface CastingLayout {
  mode: "single" | "grid";
  /** Faces on a page when in grid mode. */
  perPage: 2 | 3 | 4 | 6;
  /** Show measurements pulled from the Focus profile. */
  showMeasurements: boolean;
  /** Show the note you wrote on each face. */
  showNotes: boolean;
  /** Show the role and status pills. */
  showStatus: boolean;
}

export function defaultCasting(): CastingLayout {
  return { mode: "grid", perPage: 4, showMeasurements: false, showNotes: true, showStatus: false };
}

export function normalizeCasting(raw: unknown): CastingLayout {
  const base = defaultCasting();
  const c = (raw || {}) as Partial<CastingLayout>;
  return {
    mode: c.mode === "single" ? "single" : "grid",
    perPage: ([2, 3, 4, 6] as const).includes(c.perPage as 2) ? (c.perPage as 2 | 3 | 4 | 6) : base.perPage,
    showMeasurements: c.showMeasurements === true,
    showNotes: c.showNotes !== false,
    showStatus: c.showStatus === true,
  };
}


export interface BriefDesign {
  /** Show a designed cover page at the front. */
  cover: boolean;
  coverStyle: CoverStyle;
  orientation: BriefOrientation;
  /** Which type pairing the document is set in - see lib/brief-builder/typography. */
  fontPair?: string;
  /** Durable visual-system identity for built-in editorial templates. */
  templateId?: string;
  /** Version of the built-in layout recipe stamped onto this document. */
  layoutVersion?: number;
  /** Logo shown on the cover, usually from a brand kit. */
  logoUrl?: string;
  intro: IntroPage;
  end: EndPage;
  /** Colours for the whole document. */
  theme: PageTheme;
  /** Colour overrides for single pages, keyed by page key. */
  pageThemes: Record<string, PageTheme>;
  /** Per-element text tweaks, keyed by element key. */
  textStyles: Record<string, TextStyle>;
  /** Fixed pages the user removed (concept, talent, shoot, orphan boards) - re-addable from the page menu. */
  hiddenPages: string[];
  /** Order of the opening pages (cover, contents, project details, intro, concept, title) after the user has rearranged them. */
  pageOrder?: string[];
  /** Speaker notes shown only in presenter mode, keyed by page position. */
  presenterNotes?: Record<string, string>;
  /** How the casting board is laid out. */
  casting: CastingLayout;
  /** How many image slots a page shows, keyed by page key. Defaults to four. */
  imageSlots?: Record<string, number>;
}

function cleanColor(value: unknown): string | undefined {
  return typeof value === "string" && /^#[0-9a-fA-F]{3,8}$/.test(value.trim())
    ? value.trim()
    : undefined;
}

export function normalizeTheme(raw: unknown): PageTheme {
  const t = (raw || {}) as Partial<PageTheme>;
  const out: PageTheme = {};
  const bg = cleanColor(t.bg);
  const text = cleanColor(t.text);
  if (bg) out.bg = bg;
  if (text) out.text = text;
  return out;
}

export function defaultDesign(): BriefDesign {
  return {
    cover: true,
    coverStyle: "editorial",
    orientation: "portrait",
    fontPair: "default",
    templateId: undefined,
    layoutVersion: undefined,
    intro: {
      enabled: true,
      heading: "About us",
      body: "",
      links: [],
    },
    end: {
      enabled: true,
      heading: "Thank you",
      message: "Any questions, or anything you'd like to see more of, just say.",
      links: [],
    },
    theme: {},
    pageThemes: {},
    textStyles: {},
    hiddenPages: [],
    casting: defaultCasting(),
  };
}

/** Fill in anything missing on an older saved brief. */
export function normalizeDesign(raw: unknown): BriefDesign {
  const base = defaultDesign();
  const d = (raw || {}) as Partial<BriefDesign>;
  return {
    cover: typeof d.cover === "boolean" ? d.cover : base.cover,
    coverStyle: COVER_STYLES.some((s) => s.id === d.coverStyle)
      ? (d.coverStyle as CoverStyle)
      : base.coverStyle,
    orientation: d.orientation === "landscape" ? "landscape" : "portrait",
    fontPair: typeof d.fontPair === "string" ? d.fontPair : base.fontPair,
    templateId: typeof d.templateId === "string" ? d.templateId : undefined,
    layoutVersion: typeof d.layoutVersion === "number" && Number.isFinite(d.layoutVersion) ? d.layoutVersion : undefined,
    logoUrl: typeof d.logoUrl === "string" ? d.logoUrl : undefined,
    intro: {
      enabled: d.intro?.enabled !== false,
      heading: d.intro?.heading ?? base.intro.heading,
      body: d.intro?.body ?? base.intro.body,
      links: Array.isArray(d.intro?.links) ? (d.intro?.links as EndPageLink[]) : [],
    },
    end: {
      enabled: d.end?.enabled !== false,
      heading: d.end?.heading ?? base.end.heading,
      message: d.end?.message ?? base.end.message,
      links: Array.isArray(d.end?.links) ? (d.end?.links as EndPageLink[]) : [],
    },
    theme: normalizeTheme(d.theme),
    pageThemes: Object.fromEntries(
      Object.entries((d.pageThemes || {}) as Record<string, unknown>)
        .map(([key, value]) => [key, normalizeTheme(value)] as const)
        .filter(([, value]) => value.bg || value.text)
    ),
    textStyles: Object.fromEntries(
      Object.entries((d.textStyles || {}) as Record<string, unknown>)
        .map(([key, value]) => [key, normalizeTextStyle(value)] as const)
        .filter(([, value]) => isTextStyled(value))
    ),
    hiddenPages: Array.isArray(d.hiddenPages)
      ? (d.hiddenPages as unknown[]).filter((k): k is string => typeof k === "string")
      : [],
    pageOrder: Array.isArray(d.pageOrder)
      ? (d.pageOrder as unknown[]).filter((k): k is string => typeof k === "string")
      : undefined,
    presenterNotes: Object.fromEntries(
      Object.entries((d.presenterNotes || {}) as Record<string, unknown>)
        .filter(([, value]) => typeof value === "string" && value.trim())
        .map(([key, value]) => [key, String(value)] as const)
    ),
    casting: normalizeCasting(d.casting),
    imageSlots: Object.fromEntries(
      Object.entries((d.imageSlots || {}) as Record<string, unknown>)
        .map(([key, value]) => [key, Math.max(1, Math.min(13, Math.round(Number(value) || 4)))] as const)
        .filter(([, value]) => Number.isFinite(value))
    ),
  };
}

export interface BriefSection {
  id: string;
  type: SectionType;
  position: number;
  body: string;
  /** Custom page heading, used by pages the user added themselves. */
  heading?: string;
  /** Custom line under the heading - replaces the page's default description. */
  subtitle?: string;
  /** Structured details used by the Location page. */
  locationName?: string;
  locationAddress?: string;
  locationContact?: string;
}

/** Editable title/subtitle overrides for fixed pages, keyed by page key. */
export type PageTextMap = Record<string, { title?: string; subtitle?: string }>;

/** Extra bits the user drops onto a page with the floating + button. */
export type PageBlockKind = "text" | "link" | "image";

export interface PageBlock {
  id: string;
  kind: PageBlockKind;
  /** Starting editorial treatment chosen when a free text box is added. */
  textType?: "header" | "subheader" | "body";
  /** Starting frame proportion chosen when a free image box is added. */
  imageShape?: "square" | "horizontal" | "vertical";
  /** Text block copy, or the label shown on a link. */
  text?: string;
  /** Formatting for a free text box. */
  textStyle?: TextStyle;
  /** Link address. */
  url?: string;
  /** Image block - the id of the reference holding the picture. */
  refId?: string;
}

/** Extra blocks per page, keyed by page key. */
export type PageBlockMap = Record<string, PageBlock[]>;

/** Position and size of an element inside a fixed page, stored as percentages. */
export interface ElementLayout {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: number;
}

/** Saved layout overrides, grouped by page key then stable element key. */
export type PageLayoutMap = Record<string, Record<string, ElementLayout>>;

export function normalizePageLayouts(raw: unknown): PageLayoutMap {
  if (!raw || typeof raw !== "object") return {};
  const out: PageLayoutMap = {};
  for (const [pageKey, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!value || typeof value !== "object") continue;
    const elements: Record<string, ElementLayout> = {};
    for (const [elementKey, candidate] of Object.entries(value as Record<string, unknown>)) {
      const item = candidate as Partial<ElementLayout>;
      if (![item.x, item.y, item.width, item.height, item.rotation, item.zIndex].every((n) => typeof n === "number" && Number.isFinite(n))) continue;
      elements[elementKey] = {
        x: Math.min(100, Math.max(0, item.x as number)),
        y: Math.min(100, Math.max(0, item.y as number)),
        width: Math.min(100, Math.max(4, item.width as number)),
        height: Math.min(100, Math.max(4, item.height as number)),
        rotation: item.rotation as number,
        zIndex: Math.max(0, Math.round(item.zIndex as number)),
      };
    }
    if (Object.keys(elements).length) out[pageKey] = elements;
  }
  return out;
}

export function normalizePageBlocks(raw: unknown): PageBlockMap {
  if (!raw || typeof raw !== "object") return {};
  const out: PageBlockMap = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(value)) continue;
    const blocks = value
      .map((b) => b as Partial<PageBlock>)
      .filter((b) => b && typeof b.id === "string" && (b.kind === "text" || b.kind === "link" || b.kind === "image"))
      .map((b) => ({
        id: b.id as string,
        kind: b.kind as PageBlockKind,
        ...(b.textType === "header" || b.textType === "subheader" || b.textType === "body" ? { textType: b.textType } : {}),
        ...(b.imageShape === "square" || b.imageShape === "horizontal" || b.imageShape === "vertical" ? { imageShape: b.imageShape } : {}),
        ...(typeof b.text === "string" ? { text: b.text } : {}),
        ...(typeof b.url === "string" ? { url: b.url } : {}),
        ...(typeof b.refId === "string" ? { refId: b.refId } : {}),
      }));
    if (blocks.length) out[key] = blocks;
  }
  return out;
}

export interface ShootDetails {
  from?: string;
  to?: string;
  callTime?: string;
  location?: string;
  budget?: string;
  /** True when dates, location and budget are all still to be decided. */
  tbd?: boolean;
}

export interface BriefDoc {
  id: string;
  /** True while the brief only lives in this browser. */
  local: boolean;
  /** What kind of document this is - it shapes the setup and the pages. */
  kind?: BriefKind;
  title: string;
  /** Cover page subtitle - editable in the doc; an empty string hides it. */
  coverSubtitle?: string;
  areas: AreaId[];
  audience: AudienceId[];
  idea: string;
  /** Cover page facts - brand, client, talent attached and so on. */
  cover: CoverDetail[];
  /** Cover page, end page and orientation choices. */
  design: BriefDesign;
  shoot: ShootDetails;
  references: BriefReference[];
  talent: BriefTalent[];
  /** Editor-only casting pool, role lanes and compare tray. */
  castingWorkspace?: CastingWorkspaceState;
  sections: BriefSection[];
  /** Title/subtitle edits on the fixed pages (concept, talent, shoot, boards). */
  pageText?: PageTextMap;
  /** Extra text, link and image blocks added to pages with the + button. */
  pageBlocks?: PageBlockMap;
  /** User-arranged element geometry for the fixed-page editor. */
  pageLayouts?: PageLayoutMap;
  /** Treatments for casting photos, keyed by talent and image position. */
  imageTreatments?: Record<string, ImageTreatment>;
  updatedAt: string;
}

export function sectionsForAreas(areas: AreaId[]): SectionType[] {
  const out: SectionType[] = [];
  for (const id of areas) {
    for (const type of areaDef(id)?.sections || []) {
      if (!out.includes(type)) out.push(type);
    }
  }
  return out;
}

/**
 * A starting point for "What's the idea?" so the user edits rather than
 * stares at an empty box. Reads the title, the chosen areas and the
 * reference count - image-reading AI comes later.
 */
export function draftIdea(doc: BriefDoc): string {
  const title = doc.title.trim() || "This project";
  const areaLabels = doc.areas
    .map((a) => areaDef(a)?.label.replace(/^A |^Brief a |^Just the /, "").toLowerCase())
    .filter(Boolean) as string[];
  const refs = doc.references.length;

  const lines: string[] = [];
  lines.push(
    refs > 0
      ? `${title} is built around ${refs} reference${refs === 1 ? "" : "s"} - the mood, light and casting energy in those images set the tone for everything else.`
      : `${title} starts here. Drop references in whenever you have them and this can be rewritten around them.`
  );
  if (areaLabels.length) {
    lines.push(
      `It covers ${areaLabels.slice(0, -1).join(", ")}${areaLabels.length > 1 ? " and " : ""}${areaLabels[areaLabels.length - 1]}.`
    );
  }
  lines.push(
    "The finished work should feel considered rather than staged - real people, natural movement, and images that hold up on their own."
  );
  return lines.join("\n\n");
}

export function newId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}
